import type { InfiniteData } from '@tanstack/react-query';

const INFINITE_LIMIT_AUTO = Symbol('INFINITE_LIMIT_AUTO');

export function injectIntoInfiniteData<const T extends API.DbItem, const M>(
  items: T[],
  prevData?: InfiniteData<API.WithPage<T[], M>>,
  knownIds?: Set<API.Id>,
  cursorKey: keyof T = 'id',
  limit: number | typeof INFINITE_LIMIT_AUTO = INFINITE_LIMIT_AUTO,
) {
  const oldData: typeof prevData = {
    ...prevData,
    pages: prevData?.pages ? [...prevData.pages] : [],
    pageParams: prevData?.pageParams || [],
  };

  itemLoop: for (const newItem of items) {
    if (oldData.pages.length) {
      // Add these items to an existing set of pages
      const pages = oldData.pages;
      const currentNewestItem = pages[0]?.data?.[0] || pages[0]?.data?.[1] || pages[1]?.data?.[0];
      const currentOldestItem =
        pages[pages.length - 1]?.data?.[pages[pages.length - 1]?.data.length - 1];
      const comparator = newItem.createdAt && currentOldestItem?.createdAt ? 'createdAt' : 'id';
      const isInSet = knownIds?.has(newItem.id);

      if (
        isInSet ||
        (currentNewestItem?.[comparator] &&
          currentNewestItem[comparator] >= newItem[comparator] &&
          currentOldestItem?.[comparator] &&
          currentOldestItem[comparator] <= newItem[comparator])
      ) {
        // This ID is within the range of IDs we have already loaded. Either replace an existing one (prevent doubles), or inject it in the place it belongs (prevent out-of-order messages)
        let workCount = 1; // for inject or replace workcount = 1, but to inject AND replace workcount = 2
        let injected = false;

        for (const pageIndex in pages) {
          entryLoop: for (const entryIndex in pages[pageIndex].data) {
            const page = pages[pageIndex];
            const entry = page.data[entryIndex];

            if (
              isInSet &&
              comparator === 'createdAt' &&
              newItem[comparator] > entry[comparator] &&
              entry.id !== newItem.id &&
              injected === false
            ) {
              // The date on this item changed (eg. for DM list); move it to the top. Continue iterating to del old one
              workCount = 2;
            }

            if (newItem[comparator] > entry[comparator] && entry.id !== newItem.id && !injected) {
              // New item should be injected here
              const data = [...page.data];

              data.splice(+entryIndex, 0, newItem);

              pages.splice(+pageIndex, 1, {
                ...page,
                pageInfo: {
                  ...page.pageInfo,
                  startCursor:
                    +pageIndex === 0 ? (newItem[cursorKey] as API.Id) : page.pageInfo.startCursor,
                },
                data,
              });

              knownIds?.add(newItem.id);

              injected = true;

              if (--workCount <= 0) {
                const pageParams = oldData.pageParams;
                // pageParams[pageIndex] = ???;

                oldData.pageParams = pageParams;
                oldData.pages = pages;

                continue itemLoop;
              }

              continue entryLoop;
            } else if (entry.id === newItem.id) {
              // Same item, replace it (or remove it if already injected above)
              const data = [...page.data];

              if (injected) {
                // We already injected an updated version of this message (with createdAt changed). Delete this old item
                data.splice(+entryIndex, 1);

                pages.splice(+pageIndex, 1, {
                  ...page,
                  pageInfo: {
                    ...page.pageInfo,
                    startCursor:
                      +pageIndex === 0
                        ? (page.data[+entryIndex + 1]?.[cursorKey] as API.Id) ||
                          page.pageInfo.startCursor
                        : page.pageInfo.startCursor,
                  },
                  data,
                });
              } else {
                // Replace this old item with this new updated version

                injected = true;

                data.splice(+entryIndex, 1, {
                  ...entry,
                  ...newItem,
                });

                pages.splice(+pageIndex, 1, {
                  ...page,
                  data,
                });
              }

              if (--workCount <= 0) {
                oldData.pages = pages;

                continue itemLoop;
              }

              continue entryLoop;
            }
          }
        }

        if (injected) {
          continue itemLoop;
        }
      }

      // If we end up here, somehow we didn't find a place to inject these. Just add them on top.

      knownIds?.add(newItem.id);

      const data = [newItem, ...pages[0].data];

      pages.splice(0, 1, {
        ...pages[0],
        pageInfo: {
          ...pages[0].pageInfo,
          startCursor: newItem[cursorKey] as API.Id,
        },
        data,
      });

      oldData.pageParams = [
        { cursor: newItem[cursorKey], limit: limit === INFINITE_LIMIT_AUTO ? data.length : limit },
        ...oldData.pageParams,
      ];
      oldData.pages = pages;

      continue itemLoop;
    }

    // We didn't have any pages, so add it on top.

    oldData.pageParams = [
      { cursor: newItem[cursorKey], limit: limit === INFINITE_LIMIT_AUTO ? 1 : limit },
    ];
    oldData.pages = [
      {
        data: [newItem],
        pageInfo: {
          startCursor: newItem[cursorKey] as API.Id,
          hasNextPage: false,
          limit: 1,
          order: { by: 'id', dir: 'DESC' },
          total: 1,
        },
      },
    ];
  }

  return oldData;
}

export function injectIntoInfiniteData2<const T extends API.DbItem, const M>(
  items: T[],
  _prevData: API.WithPage<T[], M>,
  oldData: API.WithPage<T[], M>,
  knownIds?: React.MutableRefObject<Set<number>>,
  cursorKey: keyof T = 'id',
  _limit: number | typeof INFINITE_LIMIT_AUTO = INFINITE_LIMIT_AUTO,
) {
  let modified = false;

  itemLoop: for (const newItem of items) {
    if (oldData?.data.length) {
      const currentNewestItem = oldData.data[0];
      const currentOldestItem = oldData.data[oldData.data.length - 1];
      const comparator = newItem.createdAt && currentOldestItem?.createdAt ? 'createdAt' : 'id';
      const isInSet = knownIds?.current?.has(newItem.id);

      if (
        isInSet ||
        (currentNewestItem?.[comparator] &&
          currentNewestItem[comparator] >= newItem[comparator] &&
          currentOldestItem?.[comparator] &&
          currentOldestItem[comparator] <= newItem[comparator])
      ) {
        // This ID is within the range of IDs we have already loaded. Either replace an existing one (prevent doubles), or inject it in the place it belongs (prevent out-of-order messages)
        let workCount = 1; // for inject or replace workcount = 1, but to inject AND replace workcount = 2
        let injected = false;

        entryLoop: for (const entryIndex in oldData.data) {
          const entry = oldData.data[entryIndex];

          if (
            isInSet &&
            comparator === 'createdAt' &&
            newItem[comparator] > entry[comparator] &&
            entry.id !== newItem.id &&
            injected === false
          ) {
            // The date on this item changed (eg. for DM list); move it to the top. Continue iterating to del old one
            workCount = 2;
          }

          if (newItem[comparator] > entry[comparator] && entry.id !== newItem.id && !injected) {
            // New item should be injected here
            oldData.data.splice(+entryIndex, 0, newItem);

            if (+entryIndex === 0) {
              oldData.pageInfo.startCursor = newItem[cursorKey] as API.Id;
            }

            knownIds?.current?.add(newItem.id);

            injected = true;
            modified = true;

            if (--workCount <= 0) {
              continue itemLoop;
            }

            continue entryLoop;
          } else if (entry.id === newItem.id) {
            // Same item, replace it (or remove it if already injected above)
            if (injected) {
              // We already injected an updated version of this message (with createdAt changed). Delete this old item
              oldData.data.splice(+entryIndex, 1);

              if (+entryIndex === 0) {
                oldData.pageInfo.startCursor = newItem[cursorKey] as API.Id;
              }
            } else {
              // Replace this old item with this new updated version

              injected = true;
              modified = true;

              oldData.data.splice(+entryIndex, 1, {
                ...entry,
                ...newItem,
              });
            }

            if (--workCount <= 0) {
              continue itemLoop;
            }

            continue entryLoop;
          }
        }

        if (injected) {
          continue itemLoop;
        }
      }

      // If we end up here, somehow we didn't find a place to inject these.

      knownIds?.current?.add(newItem.id);

      if (currentOldestItem[comparator] > newItem[comparator]) {
        // This is older than the oldest item; inject it at the end
        oldData.data.push(newItem);
        oldData.pageInfo.endCursor = newItem[cursorKey] as API.Id;
      } else {
        // Otherwise, inject it as the newest item
        oldData.data.unshift(newItem);
        oldData.pageInfo.startCursor = newItem[cursorKey] as API.Id;
      }

      modified = true;

      continue itemLoop;
    }

    // We didn't have any pages, so add it on top.

    oldData.data = [newItem];
    oldData.pageInfo = {
      endCursor: newItem[cursorKey] as API.Id,
      startCursor: newItem[cursorKey] as API.Id,
      hasNextPage: false,
      limit: 1,
      order: { by: 'id', dir: 'DESC' },
      total: 1,
    };

    modified = true;
  }

  return modified ? oldData : null;
}
